import { Link } from "react-router-dom";
import { createContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useUser } from "../hooks/useUser";

// Mobile header slot — pages (e.g. the creator dashboard's section menu) can
// portal their own chrome into the global header on mobile, so we render
// one combined top bar instead of two stacked ones.
export const MobileHeaderSlotContext = createContext<HTMLDivElement | null>(null);

export function Layout({ children }: { children: ReactNode }) {
  const { user, loading } = useUser();
  const headerRef = useRef<HTMLElement | null>(null);
  const [mobileSlotEl, setMobileSlotEl] = useState<HTMLDivElement | null>(null);

  // Expose live header height as --header-h so fixed/sticky descendants
  // (mobile map, sticky tabs) can anchor themselves below the chrome.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const setVar = () => {
      const h = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--header-h", `${Math.round(h)}px`);
    };
    setVar();
    const ro = new ResizeObserver(setVar);
    ro.observe(el);
    window.addEventListener("orientationchange", setVar);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", setVar);
    };
  }, []);

  return (
    <MobileHeaderSlotContext.Provider value={mobileSlotEl}>
      <div className="min-h-dvh flex flex-col">
        <header
          ref={headerRef}
          className="sticky top-0 z-50 border-b border-border bg-paper/85 backdrop-blur pt-safe"
        >
          <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3.5">
            <Link
              to="/"
              className="font-display text-xl font-semibold tracking-tighter text-ink no-underline hover:no-underline inline-flex items-center gap-2"
            >
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-primary text-white shadow-xs">
                <svg viewBox="0 0 20 20" fill="none" className="h-[60%] w-[60%]" aria-hidden>
                  <path
                    d="M10 2.25C7.10051 2.25 4.75 4.60051 4.75 7.5C4.75 11.4375 10 17.75 10 17.75C10 17.75 15.25 11.4375 15.25 7.5C15.25 4.60051 12.8995 2.25 10 2.25Z"
                    fill="currentColor"
                  />
                  <circle cx="10" cy="7.5" r="2.1" fill="#FAFAF7" />
                </svg>
              </span>
              <span>DropIn<span className="text-primary">.</span></span>
            </Link>
            {/* Desktop: sign-in / dashboard link. */}
            <nav className="ml-auto hidden sm:flex items-center gap-1.5">
              {loading ? null : user ? (
                <Link to="/dashboard" className="btn-ghost text-sm" data-testid="nav-dashboard">
                  Dashboard
                </Link>
              ) : (
                <Link to="/signin" className="btn-ghost text-sm" data-testid="nav-signin">
                  Sign in
                </Link>
              )}
            </nav>
            {/* Mobile: pages portal their section chrome (label + hamburger) into here. */}
            <div
              ref={setMobileSlotEl}
              className="ml-auto flex sm:hidden items-center gap-2 min-w-0"
            />
          </div>
        </header>
        <main className="flex-1">
          <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:py-12">{children}</div>
        </main>
        <footer className="border-t border-border py-6 text-center text-sm text-muted pb-safe">
          Made for local creators & businesses.
        </footer>
      </div>
    </MobileHeaderSlotContext.Provider>
  );
}
